/**
 * Username Service
 * Handles username validation and uniqueness checking
 */

import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from './firebase';
import { isUsernameClean } from './profanityFilter';

/**
 * Validate username format
 * Rules:
 * - 3-20 characters
 * - Alphanumeric, underscores, hyphens only
 * - Cannot start or end with underscore or hyphen
 * - No consecutive special characters
 */
export function validateUsernameFormat(username: string): { valid: boolean; error?: string } {
  if (!username || username.trim() === '') {
    return { valid: false, error: 'Username is required' };
  }

  const trimmed = username.trim();

  if (trimmed.length < 3) {
    return { valid: false, error: 'Username must be at least 3 characters' };
  }

  if (trimmed.length > 20) {
    return { valid: false, error: 'Username must be 20 characters or less' };
  }

  // Check for valid characters (alphanumeric, underscore, hyphen)
  if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
    return { valid: false, error: 'Username can only contain letters, numbers, underscores, and hyphens' };
  }

  // Cannot start or end with special characters
  if (/^[_-]|[_-]$/.test(trimmed)) {
    return { valid: false, error: 'Username cannot start or end with underscore or hyphen' };
  }

  // No consecutive special characters
  if (/[_-]{2,}/.test(trimmed)) {
    return { valid: false, error: 'Username cannot have consecutive underscores or hyphens' };
  }

  // Check for profanity
  const profanityCheck = isUsernameClean(trimmed);
  if (!profanityCheck.clean) {
    return { valid: false, error: profanityCheck.reason || 'Username contains inappropriate language' };
  }

  return { valid: true };
}

/**
 * Check if username is already taken
 * Uses the usernames collection for fast lookups
 */
export async function isUsernameTaken(username: string, excludeUserId?: string): Promise<boolean> {
  try {
    const normalizedUsername = username.trim().toLowerCase();
    
    // Check the usernames collection
    const usernameDocRef = doc(db, 'usernames', normalizedUsername);
    const usernameDoc = await getDoc(usernameDocRef);
    
    if (!usernameDoc.exists()) {
      return false;
    }
    
    // If it exists, check if it's the current user's
    const data = usernameDoc.data();
    if (excludeUserId && data.userId === excludeUserId) {
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error checking username availability:', error);
    // Return false to allow the attempt - will fail at save if duplicate
    return false;
  }
}

/**
 * Reserve a username in the usernames collection
 */
export async function reserveUsername(username: string, userId: string): Promise<void> {
  const normalizedUsername = username.trim().toLowerCase();
  const usernameDocRef = doc(db, 'usernames', normalizedUsername);
  
  await setDoc(usernameDocRef, {
    userId: userId,
    username: username.trim(),
    createdAt: new Date()
  });
}

/**
 * Release a username from the usernames collection
 */
export async function releaseUsername(username: string): Promise<void> {
  const normalizedUsername = username.trim().toLowerCase();
  const usernameDocRef = doc(db, 'usernames', normalizedUsername);
  
  try {
    await deleteDoc(usernameDocRef);
  } catch (error) {
    console.error('Error releasing username:', error);
  }
}

/**
 * Check if username is available (combined format + uniqueness check)
 */
export async function checkUsernameAvailability(
  username: string, 
  excludeUserId?: string
): Promise<{ 
  available: boolean; 
  error?: string 
}> {
  // First check format
  const formatCheck = validateUsernameFormat(username);
  if (!formatCheck.valid) {
    return { available: false, error: formatCheck.error };
  }

  // Then check if taken
  try {
    const taken = await isUsernameTaken(username, excludeUserId);
    if (taken) {
      return { available: false, error: 'Username is already taken' };
    }
    
    return { available: true };
  } catch (error) {
    // If check fails, return available=true but let server validate
    console.warn('Username check failed, will validate on save');
    return { available: true };
  }
}
